import { AddOutlined } from '@mui/icons-material';
import { alpha, Box, Button, useTheme } from '@mui/material';
import CreateWorkspaceDrawer from 'components/drawers/create-workspace-drawer/create-workspace-drawer.component';
import { useDrawer } from 'components/drawers/inc-drawer/inc-drawer.hooks';
import AutocompleteBase from 'components/forms/fields/autocomplete-field/autocomplete-base/autocomplete-base.component';
import { useHasPermission } from 'hooks/authorization.hooks';
import { useIsFeatureFlagEnabled } from 'hooks/client-configuration.hooks';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentMerchant,
  selectMerchantList,
} from 'redux/app/merchant/merchant.selectors';
import { merchantActions } from 'redux/app/merchant/merchant.slice';
import { getPersistor } from 'redux/store';
import { withLabelAndId } from 'util/autocomplete';
import { FeatureFlag } from 'util/client-configuration';
import { assign } from 'util/location';

interface WorkspaceSelectorProps {
  /** Recolors the field for the navy/purple sidebar gradient surface. */
  inSidebar?: boolean;
}

const WorkspaceSelector = ({ inSidebar = false }: WorkspaceSelectorProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const drawer = useDrawer();
  const isWorkspacesFeatureEnabled = useIsFeatureFlagEnabled(
    FeatureFlag.Workspaces,
  );

  const merchants = useSelector(selectMerchantList);
  const currentMerchant = useSelector(selectCurrentMerchant);
  const dispatch = useDispatch();
  const permission = useHasPermission('CREATE_MERCHANT');
  const options = useMemo(() => {
    const opts = withLabelAndId(merchants, 'title', 'merchantId') as {
      label: string;
      id: string;
      icon?: ReactNode;
    }[];
    if (permission) {
      opts.unshift({
        label: t('WorkspaceSelector.create'),
        id: 'create',
        icon: <AddOutlined />,
      });
    }
    return opts;
  }, [merchants, permission, t]);
  const defaultValue = withLabelAndId(currentMerchant, 'title', 'merchantId');

  const handleChange = async (_e, value) => {
    dispatch(merchantActions.setCurrent(value.merchantId));
    await getPersistor().flush();
    assign('/');
  };

  if (!isWorkspacesFeatureEnabled) return null;

  const white = theme.palette.common.white;
  // Scoped to the closed field only (label + outlined input), so the dropdown
  // popup keeps its default light styling.
  const sidebarSx = inSidebar
    ? {
        '& .MuiInputLabel-root': {
          color: alpha(white, 0.7),
          '&.Mui-focused': { color: white },
        },
        '& .MuiOutlinedInput-root': {
          color: white,
          backgroundColor: alpha(white, 0.12),
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(white, 0.4),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(white, 0.7),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: white,
          },
        },
        '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator':
          {
            color: alpha(white, 0.7),
          },
      }
    : undefined;

  return (
    <>
      <AutocompleteBase
        options={options}
        defaultValue={defaultValue}
        data-testid="WorkspaceSelector.AutocompleteBase"
        onChange={handleChange}
        isOptionEqualToValue={(option, value) =>
          option.merchantId === value.merchantId
        }
        disableClearable
        sx={sidebarSx}
        TextFieldProps={{
          label: t('WorkspaceSelector.label'),
        }}
        renderOption={(props, option) =>
          option.icon ? (
            <Box
              component="li"
              data-testid="Autocomplete.Option"
              px={2}
              py={1}
              key="create"
            >
              <Button
                variant="contained"
                startIcon={option.icon}
                size="small"
                fullWidth
                onClick={drawer.open}
              >
                {option.label}
              </Button>
            </Box>
          ) : (
            <li data-testid="Autocomplete.Option" {...props}>
              {option.label}
            </li>
          )
        }
      />
      <CreateWorkspaceDrawer {...drawer} />
    </>
  );
};

export default WorkspaceSelector;
